"use client";

import { Mesh, Program, Triangle } from "ogl";
import * as React from "react";
import { cn } from "../../lib";
import { tryCreateOglRenderer } from "./try-create-ogl-renderer";

export type GrainientTheme = "purple" | "orange" | "gold" | "blue";

const GRAINIENT_THEMES: Record<
	GrainientTheme,
	{ color1: string; color2: string; color3: string }
> = {
	purple: {
		color1: "#FF9FFC",
		color2: "#5227FF",
		color3: "#B19EEF",
	},
	orange: {
		color1: "#ff9300",
		color2: "#ff6a00",
		color3: "#fec700",
	},
	gold: {
		color1: "#ffac47",
		color2: "#D99800",
		color3: "#a96800",
	},
	/** Battle encounter-stats MP bar (`#14ADFF` → `#006BA4`). */
	blue: {
		color1: "#5ecfff",
		color2: "#14ADFF",
		color3: "#006BA4",
	},
};

export interface GrainientProps extends React.HTMLAttributes<HTMLDivElement> {
	theme?: GrainientTheme;
	/** Opacity used when light mode is active (default matches TopicLearnerSwiper). */
	lightOpacity?: number;
	/** Opacity used when dark mode is active (default matches TopicLearnerSwiper). */
	darkOpacity?: number;
	/**
	 * When true, renders a solid background color behind the WebGL canvas.
	 * Useful when the canvas is semi-transparent (`alpha: true`).
	 */
	withBackground?: boolean;
	/** Background color used when `withBackground` is true. */
	backgroundColor?: string;
	timeSpeed?: number;
	colorBalance?: number;
	warpStrength?: number;
	warpFrequency?: number;
	warpSpeed?: number;
	warpAmplitude?: number;
	blendAngle?: number;
	blendSoftness?: number;
	rotationAmount?: number;
	noiseScale?: number;
	grainAmount?: number;
	grainScale?: number;
	grainAnimated?: boolean;
	contrast?: number;
	gamma?: number;
	saturation?: number;
	centerX?: number;
	centerY?: number;
	zoom?: number;
	color1?: string;
	color2?: string;
	color3?: string;
}

function hexToRgb(hex: string): [number, number, number] {
	const result =
		/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex) ?? null;
	if (!result) {
		return [1, 1, 1];
	}
	return [
		Number.parseInt(result[1] ?? "ff", 16) / 255,
		Number.parseInt(result[2] ?? "ff", 16) / 255,
		Number.parseInt(result[3] ?? "ff", 16) / 255,
	];
}

const vertex = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragment = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform float uTimeSpeed;
uniform float uColorBalance;
uniform float uWarpStrength;
uniform float uWarpFrequency;
uniform float uWarpSpeed;
uniform float uWarpAmplitude;
uniform float uBlendAngle;
uniform float uBlendSoftness;
uniform float uRotationAmount;
uniform float uNoiseScale;
uniform float uGrainAmount;
uniform float uGrainScale;
uniform float uGrainAnimated;
uniform float uContrast;
uniform float uGamma;
uniform float uSaturation;
uniform vec2 uCenterOffset;
uniform float uZoom;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
out vec4 fragColor;
#define S(a,b,t) smoothstep(a,b,t)
mat2 Rot(float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c);} 
vec2 hash(vec2 p){p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37)));return fract(sin(p)*43758.5453);} 
float noise(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);float n=mix(mix(dot(-1.0+2.0*hash(i+vec2(0.0,0.0)),f-vec2(0.0,0.0)),dot(-1.0+2.0*hash(i+vec2(1.0,0.0)),f-vec2(1.0,0.0)),u.x),mix(dot(-1.0+2.0*hash(i+vec2(0.0,1.0)),f-vec2(0.0,1.0)),dot(-1.0+2.0*hash(i+vec2(1.0,1.0)),f-vec2(1.0,1.0)),u.x),u.y);return 0.5+0.5*n;}
void mainImage(out vec4 o, vec2 C){
  float t=iTime*uTimeSpeed;
  vec2 uv=C/iResolution.xy;
  float ratio=iResolution.x/iResolution.y;
  vec2 tuv=uv-0.5+uCenterOffset;
  tuv/=max(uZoom,0.001);

  float degree=noise(vec2(t*0.1,tuv.x*tuv.y)*uNoiseScale);
  tuv.y*=1.0/ratio;
  tuv*=Rot(radians((degree-0.5)*uRotationAmount+180.0));
  tuv.y*=ratio;

  float frequency=uWarpFrequency;
  float ws=max(uWarpStrength,0.001);
  float amplitude=uWarpAmplitude/ws;
  float warpTime=t*uWarpSpeed;
  tuv.x+=sin(tuv.y*frequency+warpTime)/amplitude;
  tuv.y+=sin(tuv.x*(frequency*1.5)+warpTime)/(amplitude*0.5);

  vec3 colLav=uColor1;
  vec3 colOrg=uColor2;
  vec3 colDark=uColor3;
  float b=uColorBalance;
  float s=max(uBlendSoftness,0.0);
  mat2 blendRot=Rot(radians(uBlendAngle));
  float blendX=(tuv*blendRot).x;
  float edge0=-0.3-b-s;
  float edge1=0.2-b+s;
  float v0=0.5-b+s;
  float v1=-0.3-b-s;
  vec3 layer1=mix(colDark,colOrg,S(edge0,edge1,blendX));
  vec3 layer2=mix(colOrg,colLav,S(edge0,edge1,blendX));
  vec3 col=mix(layer1,layer2,S(v0,v1,tuv.y));

  vec2 grainUv=uv*max(uGrainScale,0.001);
  if(uGrainAnimated>0.5){grainUv+=vec2(iTime*0.05);} 
  float grain=fract(sin(dot(grainUv,vec2(12.9898,78.233)))*43758.5453);
  col+=(grain-0.5)*uGrainAmount;

  col=(col-0.5)*uContrast+0.5;
  float luma=dot(col,vec3(0.2126,0.7152,0.0722));
  col=mix(vec3(luma),col,uSaturation);
  col=pow(max(col,0.0),vec3(1.0/max(uGamma,0.001)));
  col=clamp(col,0.0,1.0);

  o=vec4(col,1.0);
}
void main(){
  vec4 o=vec4(0.0);
  mainImage(o,gl_FragCoord.xy);
  fragColor=o;
}
`;

export function Grainient({
	theme = "purple",
	lightOpacity = 0.8,
	darkOpacity = 0.3,
	withBackground = false,
	backgroundColor = "#141924",
	timeSpeed = 0.25,
	colorBalance = 0,
	warpStrength = 1,
	warpFrequency = 5,
	warpSpeed = 2,
	warpAmplitude = 50,
	blendAngle = 0,
	blendSoftness = 0.05,
	rotationAmount = 500,
	noiseScale = 2,
	grainAmount = 0.1,
	grainScale = 2,
	grainAnimated = false,
	contrast = 1.5,
	gamma = 1,
	saturation = 1,
	centerX = 0,
	centerY = 0,
	zoom = 0.9,
	color1,
	color2,
	color3,
	className,
	...props
}: GrainientProps) {
	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const themeColors = GRAINIENT_THEMES[theme];
	const resolvedColor1 = color1 ?? themeColors.color1;
	const resolvedColor2 = color2 ?? themeColors.color2;
	const resolvedColor3 = color3 ?? themeColors.color3;

	React.useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const renderer = tryCreateOglRenderer({
			webgl: 2,
			alpha: true,
			antialias: false,
			dpr: Math.min(window.devicePixelRatio || 1, 2),
		});
		if (!renderer) {
			return;
		}

		const gl = renderer.gl;
		const canvas = gl.canvas as HTMLCanvasElement;
		canvas.style.width = "100%";
		canvas.style.height = "100%";
		canvas.style.display = "block";

		container.appendChild(canvas);

		const geometry = new Triangle(gl);
		const program = new Program(gl, {
			vertex,
			fragment,
			uniforms: {
				iTime: { value: 0 },
				iResolution: { value: new Float32Array([1, 1]) },
				uTimeSpeed: { value: timeSpeed },
				uColorBalance: { value: colorBalance },
				uWarpStrength: { value: warpStrength },
				uWarpFrequency: { value: warpFrequency },
				uWarpSpeed: { value: warpSpeed },
				uWarpAmplitude: { value: warpAmplitude },
				uBlendAngle: { value: blendAngle },
				uBlendSoftness: { value: blendSoftness },
				uRotationAmount: { value: rotationAmount },
				uNoiseScale: { value: noiseScale },
				uGrainAmount: { value: grainAmount },
				uGrainScale: { value: grainScale },
				uGrainAnimated: { value: grainAnimated ? 1 : 0 },
				uContrast: { value: contrast },
				uGamma: { value: gamma },
				uSaturation: { value: saturation },
				uCenterOffset: { value: new Float32Array([centerX, centerY]) },
				uZoom: { value: zoom },
				uColor1: {
					value: new Float32Array(hexToRgb(resolvedColor1)),
				},
				uColor2: {
					value: new Float32Array(hexToRgb(resolvedColor2)),
				},
				uColor3: {
					value: new Float32Array(hexToRgb(resolvedColor3)),
				},
			},
		});

		const mesh = new Mesh(gl, { geometry, program });

		let lastWidth = 0;
		let lastHeight = 0;

		const setSize = () => {
			const rect = container.getBoundingClientRect();
			// Round up: ogl writes the canvas CSS size in px, so flooring a
			// fractional container width leaves an unpainted sliver on the edge.
			const width = Math.max(1, Math.ceil(rect.width));
			const height = Math.max(1, Math.ceil(rect.height));

			// During layout/resize transitions the element can momentarily report 0x0.
			// Avoid resizing the GL backbuffer to 0 (causes a visible "blink").
			if (width === 1 && rect.width <= 0) {
				return;
			}
			if (height === 1 && rect.height <= 0) {
				return;
			}

			if (width === lastWidth && height === lastHeight) {
				return;
			}
			lastWidth = width;
			lastHeight = height;

			renderer.setSize(width, height);

			const res = (
				program.uniforms.iResolution as { value: Float32Array }
			).value;
			res[0] = gl.drawingBufferWidth;
			res[1] = gl.drawingBufferHeight;

			// Render immediately so we don't flash transparent between resize + next RAF tick.
			renderer.render({ scene: mesh });
		};

		let needsResize = true;
		const ro = new ResizeObserver(() => {
			needsResize = true;
		});
		ro.observe(container);
		setSize();

		let raf = 0;
		const t0 = performance.now();
		const loop = (t: number) => {
			if (needsResize) {
				needsResize = false;
				setSize();
			}
			(program.uniforms.iTime as { value: number }).value =
				(t - t0) * 0.001;
			renderer.render({ scene: mesh });
			raf = requestAnimationFrame(loop);
		};
		raf = requestAnimationFrame(loop);

		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
			try {
				container.removeChild(canvas);
			} catch {
				// ignore
			}
		};
	}, [
		theme,
		timeSpeed,
		colorBalance,
		warpStrength,
		warpFrequency,
		warpSpeed,
		warpAmplitude,
		blendAngle,
		blendSoftness,
		rotationAmount,
		noiseScale,
		grainAmount,
		grainScale,
		grainAnimated,
		contrast,
		gamma,
		saturation,
		centerX,
		centerY,
		zoom,
		resolvedColor1,
		resolvedColor2,
		resolvedColor3,
	]);

	return (
		<div
			className={cn("relative h-full w-full overflow-hidden", className)}
			data-sq-clickspark-primary={resolvedColor2}
			style={{
				...(props.style ?? {}),
				backgroundColor: withBackground
					? backgroundColor
					: (props.style?.backgroundColor as string | undefined),
			}}
			{...props}
		>
			<div
				ref={containerRef}
				className="absolute inset-0 opacity-[var(--sq-grainient-opacity)] dark:opacity-[var(--sq-grainient-opacity-dark)]"
				style={{
					["--sq-grainient-opacity" as never]: String(
						Math.max(0, Math.min(1, lightOpacity)),
					),
					["--sq-grainient-opacity-dark" as never]: String(
						Math.max(0, Math.min(1, darkOpacity)),
					),
				}}
			/>
		</div>
	);
}
